import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { X, Loader2, Check, Mail, UserPlus, AlertTriangle } from 'lucide-react';
import { SUPPORT_AREAS, normalizeSupportAreaArray } from '../constants/supportAreas';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const TIERS = ['Tier 1', 'Tier 2', 'Tier 3'];
const STATUSES = ['Improving', 'Limited Progress', 'Needs Review'];

export default function EditStudentModal({ student, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [staffContacts, setStaffContacts] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [teacherEntryMode, setTeacherEntryMode] = useState('select'); // 'select' or 'email'
  const [teacherEmail, setTeacherEmail] = useState('');
  const [teacherDisplayName, setTeacherDisplayName] = useState('');
  const [creatingTeacher, setCreatingTeacher] = useState(false);
  
  // Determine current teacher state from student data
  const currentTeacherContact = student.teacher_contact || null;
  const hasStructuredTeacherWithEmail = currentTeacherContact?.email ? true : false;
  const hasStructuredTeacherWithoutEmail = currentTeacherContact && !currentTeacherContact.email;
  const hasLegacyTeacherNameOnly = !currentTeacherContact && student.teacher_name;
  
  const [formData, setFormData] = useState({
    first_name: student.first_name,
    last_name: student.last_name,
    student_id_number: student.student_id_number || '',
    grade: student.grade,
    teacher_name: student.teacher_name || '',
    teacher_contact_id: student.teacher_contact_id || '',
    school_name: student.school_name || '',
    concern_area: normalizeSupportAreaArray(student.concern_area || []),
    current_tier: student.current_tier,
    support_start_date: student.support_start_date || '',
    review_date: student.review_date || '',
    status: student.status,
    notes: student.notes || '',
    active: student.active
  });

  // Load staff contacts for the student's school
  useEffect(() => {
    if (student.school_id) {
      loadStaffContacts(student.school_id);
    }
  }, [student.school_id]);

  const loadStaffContacts = async (schoolId) => {
    setLoadingStaff(true);
    try {
      const res = await apiService.getStaffContacts({ school_id: schoolId, staff_type: 'classroom_teacher' });
      setStaffContacts(res.data);
    } catch (error) {
      console.error('Failed to load staff contacts:', error);
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleConcernToggle = (concern) => {
    const current = formData.concern_area;
    if (current.includes(concern)) {
      handleChange('concern_area', current.filter(c => c !== concern));
    } else {
      handleChange('concern_area', [...current, concern]);
    }
  };

  // Determine if teacher assignment is valid for save
  const hasValidTeacherAssignment = () => {
    if (teacherEntryMode === 'email') {
      return teacherEmail && teacherEmail.includes('@');
    } else {
      // In select mode, must have a contact with email selected
      if (formData.teacher_contact_id) {
        const selectedContact = staffContacts.find(c => c.id === formData.teacher_contact_id);
        return selectedContact?.email ? true : false;
      }
      // If current teacher has email, it's valid
      return hasStructuredTeacherWithEmail;
    }
  };

  // Check if legacy record needs teacher assignment
  const needsTeacherAssignment = hasStructuredTeacherWithoutEmail || hasLegacyTeacherNameOnly;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.first_name || !formData.last_name || !formData.grade) {
      toast.error('Please fill in required fields');
      return;
    }

    // Validate teacher assignment for legacy records
    if (needsTeacherAssignment && !hasValidTeacherAssignment()) {
      toast.error('Please select or add a teacher with email to update this student');
      return;
    }

    setLoading(true);
    try {
      let finalFormData = { ...formData };
      
      // If using email entry mode and email is provided, find or create staff contact
      if (teacherEntryMode === 'email' && teacherEmail && teacherEmail.includes('@')) {
        setCreatingTeacher(true);
        try {
          const res = await apiService.findOrCreateStaffContact({
            school_id: student.school_id,
            email: teacherEmail.toLowerCase().trim(),
            display_name: teacherDisplayName || null
          });
          finalFormData.teacher_contact_id = res.data.id;
          finalFormData.teacher_name = res.data.display_name;
        } catch (error) {
          console.error('Failed to create teacher contact:', error);
          toast.error(error.response?.data?.detail || 'Failed to link teacher');
          setLoading(false);
          setCreatingTeacher(false);
          return;
        }
        setCreatingTeacher(false);
      }
      
      await apiService.updateStudent(student.id, finalFormData);
      toast.success('Student updated successfully');
      onSave();
    } catch (error) {
      console.error('Failed to update student:', error);
      toast.error('Failed to update student');
    } finally {
      setLoading(false);
      setCreatingTeacher(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }} data-testid="edit-student-modal">
        <div className="modal-header">
          <h2 className="modal-title">Edit Student</h2>
          <button className="modal-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="form-grid">
              <div className="form-group">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  required
                  data-testid="edit-first-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  required
                  data-testid="edit-last-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="student_id_number">Student ID #</Label>
                <Input
                  id="student_id_number"
                  value={formData.student_id_number}
                  onChange={(e) => handleChange('student_id_number', e.target.value)}
                  placeholder="School's student ID"
                  maxLength={50}
                  data-testid="edit-student-id-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="grade">Grade *</Label>
                <Select value={formData.grade} onValueChange={(v) => handleChange('grade', v)}>
                  <SelectTrigger data-testid="edit-grade-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADES.map((grade) => (
                      <SelectItem key={grade} value={grade}>Grade {grade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="teacher_contact">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Teacher Email
                  </div>
                </Label>
                <p className="text-xs text-[#595959] mb-2">
                  Teacher email is required so student assignments, progress updates, and future messaging stay connected correctly.
                </p>
                
                <div className="space-y-3">
                  {/* Mode toggle */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setTeacherEntryMode('select')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        teacherEntryMode === 'select'
                          ? 'bg-[#2C4C3B] text-white border-[#2C4C3B]'
                          : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-[#2C4C3B]'
                      }`}
                    >
                      Select Existing
                    </button>
                    <button
                      type="button"
                      onClick={() => setTeacherEntryMode('email')}
                      className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                        teacherEntryMode === 'email'
                          ? 'bg-[#2C4C3B] text-white border-[#2C4C3B]'
                          : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-[#2C4C3B]'
                      }`}
                    >
                      <UserPlus className="h-4 w-4 inline mr-1" />
                      Add by Email
                    </button>
                  </div>

                  {teacherEntryMode === 'select' ? (
                    <>
                      {/* Show current teacher with email - properly preselected */}
                      {hasStructuredTeacherWithEmail && !formData.teacher_contact_id.includes(currentTeacherContact?.id) && (
                        <div className="p-2 bg-green-50 border border-green-200 rounded-lg mb-2">
                          <p className="text-xs text-green-700">
                            <strong>Current teacher:</strong> {currentTeacherContact.display_name} ({currentTeacherContact.email})
                          </p>
                        </div>
                      )}
                      
                      <Select 
                        value={formData.teacher_contact_id || ''} 
                        onValueChange={(v) => {
                          const contact = staffContacts.find(c => c.id === v);
                          setFormData(prev => ({
                            ...prev,
                            teacher_contact_id: v,
                            teacher_name: contact?.display_name || prev.teacher_name
                          }));
                        }}
                      >
                        <SelectTrigger data-testid="edit-teacher-select">
                          <SelectValue placeholder={loadingStaff ? "Loading..." : "Select a teacher with email"} />
                        </SelectTrigger>
                        <SelectContent>
                          {staffContacts.filter(c => c.email).map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              <span className="flex items-center gap-2">
                                {contact.display_name}
                                <span className="text-[#A3A3A3]">({contact.email})</span>
                              </span>
                            </SelectItem>
                          ))}
                          {staffContacts.filter(c => !c.email).length > 0 && (
                            <div className="px-2 py-1.5 text-xs text-amber-600 border-t border-[#E5E5E5] mt-1">
                              {staffContacts.filter(c => !c.email).length} teacher(s) hidden — missing email
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      
                      {/* Show selected teacher confirmation */}
                      {formData.teacher_contact_id && (() => {
                        const selectedContact = staffContacts.find(c => c.id === formData.teacher_contact_id);
                        if (selectedContact?.email) {
                          const isNewSelection = formData.teacher_contact_id !== student.teacher_contact_id;
                          return (
                            <div className={`p-2 rounded-lg ${isNewSelection ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
                              <p className={`text-xs ${isNewSelection ? 'text-blue-700' : 'text-green-700'}`}>
                                <strong>{isNewSelection ? 'Will be linked to:' : 'Selected teacher:'}</strong> {selectedContact.display_name} ({selectedContact.email})
                              </p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Warning: Current teacher contact exists but has no email */}
                      {hasStructuredTeacherWithoutEmail && !formData.teacher_contact_id && (
                        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-amber-700">
                            <strong>Current teacher needs email:</strong> "{currentTeacherContact.display_name}"<br/>
                            Select a teacher with email above to update this student's teacher assignment.
                          </p>
                        </div>
                      )}
                      
                      {/* Warning: Legacy teacher_name only, no structured contact */}
                      {hasLegacyTeacherNameOnly && !formData.teacher_contact_id && (
                        <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-amber-700">
                            <strong>Legacy teacher:</strong> "{formData.teacher_name}"<br/>
                            Select a teacher with email above to link this student to a structured teacher contact.
                          </p>
                        </div>
                      )}
                      
                      {staffContacts.filter(c => c.email).length === 0 && !loadingStaff && (
                        <p className="text-xs text-amber-600">
                          No teachers with email found. Use "Add by Email" to create a new teacher contact.
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="space-y-3 p-3 bg-[#F3F4F1] rounded-lg border border-[#E5E5E5]">
                      <div>
                        <label className="block text-xs font-medium text-[#595959] mb-1">Teacher Email *</label>
                        <Input
                          type="email"
                          value={teacherEmail}
                          onChange={(e) => {
                            setTeacherEmail(e.target.value);
                            // Auto-suggest display name from email
                            if (e.target.value.includes('@')) {
                              const localPart = e.target.value.split('@')[0];
                              const parts = localPart.split(/[._-]/).map(p => 
                                p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()
                              );
                              setTeacherDisplayName(parts.join(' '));
                            }
                          }}
                          placeholder="teacher@school.edu"
                          className="text-sm"
                          data-testid="edit-teacher-email-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[#595959] mb-1">Display Name</label>
                        <Input
                          type="text"
                          value={teacherDisplayName}
                          onChange={(e) => setTeacherDisplayName(e.target.value)}
                          placeholder="Suggested from email"
                          className="text-sm"
                          data-testid="edit-teacher-display-name-input"
                        />
                        <p className="text-xs text-[#A3A3A3] mt-1">
                          Name will be suggested from email and can be edited.
                        </p>
                      </div>
                      {teacherEmail && teacherEmail.includes('@') && (
                        <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-700">
                            {staffContacts.find(c => c.email?.toLowerCase() === teacherEmail.toLowerCase())
                              ? <><strong>Will be linked to:</strong> {teacherDisplayName || teacherEmail.split('@')[0]} ({teacherEmail})</>
                              : <><strong>Will create and link:</strong> {teacherDisplayName || teacherEmail.split('@')[0]} ({teacherEmail})</>}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group full-width">
                <Label>Support Area(s)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {SUPPORT_AREAS.map((area) => {
                    const isSelected = formData.concern_area.includes(area);
                    return (
                      <button
                        key={area}
                        type="button"
                        onClick={() => handleConcernToggle(area)}
                        data-testid={`edit-support-area-${area.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                        className={`
                          inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium
                          transition-all duration-150 border-2
                          ${isSelected 
                            ? 'bg-[#2C4C3B] text-white border-indigo-600 shadow-sm' 
                            : 'bg-white text-[#595959] border-[#E5E5E5] hover:border-slate-300 hover:bg-[#F3F4F1]'
                          }
                        `}
                      >
                        {isSelected && <Check className="h-3.5 w-3.5" />}
                        {area}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="current_tier">Current Tier</Label>
                <Select value={formData.current_tier} onValueChange={(v) => handleChange('current_tier', v)}>
                  <SelectTrigger data-testid="edit-tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIERS.map((tier) => (
                      <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                  <SelectTrigger data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="support_start_date">Support Start Date</Label>
                <Input
                  id="support_start_date"
                  type="date"
                  value={formData.support_start_date}
                  onChange={(e) => handleChange('support_start_date', e.target.value)}
                  data-testid="edit-start-date-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="review_date">Review Date</Label>
                <Input
                  id="review_date"
                  type="date"
                  value={formData.review_date}
                  onChange={(e) => handleChange('review_date', e.target.value)}
                  data-testid="edit-review-date-input"
                />
              </div>

              <div className="form-group full-width">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={3}
                  data-testid="edit-notes-input"
                />
              </div>

              <div className="form-group full-width">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => handleChange('active', checked)}
                    data-testid="edit-active-checkbox"
                  />
                  <label htmlFor="active" className="text-sm text-[#1C1C1C] cursor-pointer">
                    Active Student
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#2C4C3B] hover:bg-[#1E3629]"
              disabled={loading}
              data-testid="save-edit-student-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
